/** Mirrors backend `GstCalculator` for POS display only. */

export type GstCustomer = { gstin?: string | null } | null;
export type GstBranch = { gstin?: string | null; state_code?: string | null };

export type GstLineInput = {
  rate: number;
  qty: number;
  disc_pct: number;
  gst_rate: number;
};

export type GstLineOut = GstLineInput & {
  taxable: number;
  cgst_pct: number;
  cgst_amount: number;
  sgst_pct: number;
  sgst_amount: number;
  igst_pct: number;
  igst_amount: number;
  total: number;
};

export type GstSummary = {
  subtotal: number;
  discount_amount: number;
  taxable_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  total_amount: number;
  round_off: number;
  net_amount: number;
};

export function isInterState(customer: GstCustomer, branch: GstBranch): boolean {
  const custGst = (customer?.gstin ?? "").toUpperCase().trim();
  const brGst = (branch.gstin ?? "").toUpperCase().trim();
  if (custGst.length < 2) {
    return false;
  }
  const cState = custGst.slice(0, 2);
  if (brGst.length >= 2) {
    const bState = brGst.slice(0, 2);

    return cState !== bState;
  }
  const bCode = (branch.state_code ?? "").toUpperCase().trim();
  if (bCode.length === 2) {
    return cState !== bCode;
  }

  return false;
}

function buildSummary(items: GstLineOut[], discountPct: number): GstSummary {
  let subtotal = 0;
  let cgst = 0;
  let sgst = 0;
  let igst = 0;
  for (const it of items) {
    subtotal += it.taxable;
    cgst += it.cgst_amount;
    sgst += it.sgst_amount;
    igst += it.igst_amount;
  }

  const discAmt = Math.round(subtotal * (discountPct / 100) * 100) / 100;
  const taxableAmt = Math.round((subtotal - discAmt) * 100) / 100;
  const ratio = subtotal > 0 ? taxableAmt / subtotal : 1;
  cgst = Math.round(cgst * ratio * 100) / 100;
  sgst = Math.round(sgst * ratio * 100) / 100;
  igst = Math.round(igst * ratio * 100) / 100;

  const totalPreRound = taxableAmt + cgst + sgst + igst;
  const net = Math.round(totalPreRound);
  const roundOff = Math.round((net - totalPreRound) * 100) / 100;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discount_amount: discAmt,
    taxable_amount: taxableAmt,
    cgst_amount: cgst,
    sgst_amount: sgst,
    igst_amount: igst,
    total_amount: Math.round(totalPreRound * 100) / 100,
    round_off: roundOff,
    net_amount: net,
  };
}

export function calculateGst(
  items: GstLineInput[],
  customer: GstCustomer,
  branch: GstBranch,
  discountPct = 0,
): { items: GstLineOut[]; summary: GstSummary } {
  const interstate = isInterState(customer, branch);
  const out: GstLineOut[] = [];

  for (const item of items) {
    const rate = item.rate;
    const qty = item.qty;
    const discPct = item.disc_pct;
    const gstRate = item.gst_rate;
    const lineBase = rate * qty;
    const taxable = lineBase * (1 - discPct / 100);

    const row: GstLineOut = {
      ...item,
      taxable,
      cgst_pct: 0,
      cgst_amount: 0,
      sgst_pct: 0,
      sgst_amount: 0,
      igst_pct: 0,
      igst_amount: 0,
      total: 0,
    };

    if (interstate) {
      row.igst_pct = gstRate;
      row.igst_amount = Math.round((taxable * gstRate) / 100 * 100) / 100;
    } else {
      const half = gstRate / 2;
      row.cgst_pct = half;
      row.sgst_pct = half;
      row.cgst_amount = Math.round((taxable * half) / 100 * 100) / 100;
      row.sgst_amount = Math.round((taxable * half) / 100 * 100) / 100;
    }

    row.total =
      Math.round((row.taxable + row.cgst_amount + row.sgst_amount + row.igst_amount) * 100) / 100;
    out.push(row);
  }

  return { items: out, summary: buildSummary(out, discountPct) };
}
